import React, { useCallback, useReducer } from "react";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Button } from "components";
import { produce } from "immer";
import { useTranslation } from "react-i18next";

interface PromiseRef {
  resolve: () => void;
  reject: () => void;
}

type DialogServiceType = "Alert" | "Confirm";

export interface DialogServiceOption {
  title?: string;
  content?: React.ReactNode;
  okLabel?: string;
  cancelLabel?: string;
  type?: DialogServiceType;
  disableCloseOnBackdropAndEscape?: boolean;
}

export enum ActionTypes {
  OPEN,
  CLOSE,
  RESET,
}

export interface DialogServiceState extends DialogServiceOption {
  open: boolean;
}

export const DialogContext = React.createContext<
  (options: DialogServiceOption) => Promise<void>
  // eslint-disable-next-line @typescript-eslint/unbound-method
>(Promise.reject);

type ActionType =
  | {
      type: ActionTypes.OPEN;
      payload: DialogServiceOption;
    }
  | {
      type: ActionTypes.CLOSE;
    };

const initialState: DialogServiceState = {
  open: false,
};

const reducer = produce((draft: DialogServiceState, action: ActionType) => {
  switch (action.type) {
    case ActionTypes.OPEN: {
      draft = initialState;
      draft = { ...draft, ...action.payload };
      draft.open = true;
      break;
    }
    case ActionTypes.CLOSE: {
      draft.open = false;
      break;
    }
    default: {
      draft = initialState;
      break;
    }
  }
  return draft;
});

const DialogServiceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const [dialogServiceState, dispatch] = useReducer(reducer, initialState);
  const promiseRef = React.useRef<PromiseRef>();

  const openDialogHanlder = useCallback((options: DialogServiceOption) => {
    dispatch({ type: ActionTypes.OPEN, payload: options });
    return new Promise<void>((resolve, reject) => {
      promiseRef.current = { resolve, reject };
    });
  }, []);

  const closeDialogHanlder = () => {
    dispatch({ type: ActionTypes.CLOSE });
  };

  const onOkHandler = () => {
    promiseRef.current?.resolve?.();
    closeDialogHanlder();
  };

  const onCancelHandler = () => {
    promiseRef.current?.reject?.();
    closeDialogHanlder();
  };

  const {
    open,
    title,
    okLabel,
    cancelLabel,
    content,
    type,
    disableCloseOnBackdropAndEscape,
  } = dialogServiceState;

  const isComfirm = type === "Confirm";

  return (
    <>
      <DialogContext.Provider value={openDialogHanlder}>
        {children}
      </DialogContext.Provider>
      <Dialog
        open={open}
        onClose={
          disableCloseOnBackdropAndEscape ? undefined : closeDialogHanlder
        }
        fullWidth
      >
        {title && <DialogTitle>{title}</DialogTitle>}
        {content && <DialogContent>{content}</DialogContent>}
        <DialogActions>
          <Button
            color="inherit"
            variant="outlined"
            onClick={!isComfirm ? onOkHandler : onCancelHandler}
            useTypography
            size="sm"
          >
            {cancelLabel ?? t("Close")}
          </Button>
          {isComfirm && (
            <Button onClick={onOkHandler} useTypography size="sm">
              {okLabel ?? t("Confirm")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DialogServiceProvider;
